import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs,
  enableIndexedDbPersistence,
  Timestamp,
  query,
  where,
  getDoc,
  writeBatch,
  setDoc,
  doc
} from 'firebase/firestore';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updatePassword,
  deleteUser,
  initializeAuth,
  browserLocalPersistence,
  browserSessionPersistence
} from 'firebase/auth';
import { CalendarEvent } from '../types';
import { addMonths, addWeeks, addYears } from 'date-fns';
import { normalizeDate } from '../utils/dateUtils';

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyBgtHdFR3nmEExepN09QGXjMNkJMO0dEJ0",
  authDomain: "websimbudet.firebaseapp.com",
  projectId: "websimbudet",
  storageBucket: "websimbudet.firebasestorage.app",
  messagingSenderId: "606669062953",
  appId: "1:606669062953:web:21b1fd1fd69a295f1f2d38"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firestore
export const db = getFirestore(app);

// Initialize Auth with persistence
export const auth = initializeAuth(app, {
  persistence: [browserLocalPersistence, browserSessionPersistence]
});

// Enable offline persistence BEFORE any other Firestore operations
try {
  enableIndexedDbPersistence(db)
    .then(() => {
      console.log('Offline persistence enabled successfully');
    })
    .catch((err) => {
      if (err.code === 'failed-precondition') {
        console.warn('Multiple tabs open, persistence can only be enabled in one tab at a time.');
      } else if (err.code === 'unimplemented') {
        console.warn('The current browser doesn\'t support offline persistence.');
      }
    });
} catch (error) {
  console.warn('Error enabling persistence:', error);
}

// Authentication functions
export async function signIn(email: string, password: string) {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Get or create user document
    const userRef = doc(db, 'users', userCredential.user.uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      if (userData.status === 'pending') {
        await firebaseSignOut(auth);
        throw new Error('Your account is pending approval.');
      } else if (userData.status === 'rejected') {
        await firebaseSignOut(auth);
        throw new Error('Your account has been rejected.');
      }

      // Make ogoudge@outlook.com an admin if not already
      if (email === 'ogoudge@outlook.com' && !userData.isAdmin) {
        await updateDoc(userRef, {
          isAdmin: true,
          updatedAt: Timestamp.now()
        });
      }
    } else {
      // Create user document if it doesn't exist
      await setDoc(userRef, {
        uid: userCredential.user.uid,
        email: email,
        createdAt: Timestamp.now(),
        status: email === 'ogoudge@outlook.com' ? 'active' : 'pending',
        isAdmin: email === 'ogoudge@outlook.com',
        updatedAt: Timestamp.now()
      });
    }
    
    return userCredential.user;
  } catch (error) {
    throw new Error(getAuthErrorMessage(error instanceof Error ? error : new Error('Unknown error')));
  }
}

export async function createUser(email: string, password: string) {
  try {
    // Get referral code from URL if it exists
    const urlParams = new URLSearchParams(window.location.search);
    const incomingReferralCode = urlParams.get('ref');

    // Check if user already exists
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      throw new Error('User with this email already exists');
    }

    // If referral code exists, find the referrer
    let referrerId = null;
    if (incomingReferralCode) {
      const referrerQuery = query(usersRef, where('referralCode', '==', incomingReferralCode));
      const referrerSnapshot = await getDocs(referrerQuery);
      if (!referrerSnapshot.empty) {
        referrerId = referrerSnapshot.docs[0].id;
      }
    }

    // Check if approval is required
    const settingsDoc = await getDoc(doc(db, 'settings', 'system'));
    const requireApproval = settingsDoc.exists() ? settingsDoc.data()?.requireApproval : true;

    // Create the user account
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Generate a unique referral code
    let newUserReferralCode = generateReferralCode();
    let isUnique = false;
    
    while (!isUnique) {
      const existingCodeQuery = query(usersRef, where('referralCode', '==', newUserReferralCode));
      const existingCodeSnapshot = await getDocs(existingCodeQuery);
      if (existingCodeSnapshot.empty) {
        isUnique = true;
      } else {
        newUserReferralCode = generateReferralCode();
      }
    }

    // Create user document with referral information and admin status for ogoudge@outlook.com
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      uid: userCredential.user.uid,
      email: email,
      createdAt: Timestamp.now(),
      status: email === 'ogoudge@outlook.com' ? 'active' : (requireApproval ? 'pending' : 'active'),
      isAdmin: email === 'ogoudge@outlook.com',
      referralCode: newUserReferralCode,
      referredBy: referrerId,
      updatedAt: Timestamp.now()
    });

    if (requireApproval && email !== 'ogoudge@outlook.com') {
      await firebaseSignOut(auth);
      throw new Error('Your account is pending approval. You will be notified when your account is approved.');
    }

    return userCredential.user;
  } catch (error) {
    throw new Error(getAuthErrorMessage(error instanceof Error ? error : new Error('Unknown error')));
  }
}

export async function signOut() {
  try {
    // Clear impersonation data if it exists
    localStorage.removeItem('impersonation');
    await firebaseSignOut(auth);
  } catch (error) {
    throw new Error(getAuthErrorMessage(error instanceof Error ? error : new Error('Unknown error')));
  }
}

// Impersonation functions
export function startImpersonation(adminUid: string, adminEmail: string, targetUserId: string, targetEmail: string) {
  const impersonationData = {
    adminUid,
    adminEmail,
    targetUserId,
    targetEmail,
    startedAt: new Date().toISOString()
  };
  localStorage.setItem('impersonation', JSON.stringify(impersonationData));
}

export function endImpersonation() {
  localStorage.removeItem('impersonation');
  // Set flag to navigate to admin after reload
  localStorage.setItem('returnToAdmin', 'true');
  // Force a page reload to switch back to admin
  window.location.reload();
}

export function getImpersonationData() {
  const data = localStorage.getItem('impersonation');
  return data ? JSON.parse(data) : null;
}

export function isImpersonating(): boolean {
  return !!localStorage.getItem('impersonation');
}

// Get the effective user ID (impersonated user if impersonating, otherwise current user)
export function getEffectiveUserId(): string | null {
  const impersonation = getImpersonationData();
  if (impersonation) {
    return impersonation.targetUserId;
  }
  return auth.currentUser?.uid || null;
}

export async function deleteUserAccount(userId: string): Promise<boolean> {
  if (!auth.currentUser) {
    throw new Error('You must be logged in to perform this action');
  }

  try {
    // Get user document
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    // Delete user data from all collections
    const collections = [
      'events',
      'debts',
      'subscriptions',
      'vacationPlans',
      'supportTickets',
      'seenMessages',
      'trophies'
    ];

    const batch = writeBatch(db);

    for (const collectionName of collections) {
      const q = query(
        collection(db, collectionName),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(q);
      snapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
    }

    // Delete user document
    batch.delete(userRef);

    // Store deletion record
    batch.set(doc(db, 'deletedUsers', userId), {
      email: userDoc.data().email,
      deletedAt: Timestamp.now(),
      deletedBy: auth.currentUser.uid
    });

    await batch.commit();
    return true;
  } catch (error) {
    console.error('Error in deleteUserAccount:', error);
    return false;
  }
}

// Function to validate event data
function validateEventData(event: CalendarEvent): void {
  if (!event || typeof event !== 'object') {
    throw new Error('Invalid event data');
  }

  // Required fields
  if (!event.id?.trim()) {
    throw new Error('Event ID is required');
  }

  if (!event.title?.trim()) {
    throw new Error('Event title is required');
  }

  if (typeof event.amount !== 'number' || isNaN(event.amount)) {
    throw new Error('Event amount must be a valid number');
  }

  if (!event.date) {
    throw new Error('Event date is required');
  }

  if (!event.type || !['payday', 'bill', 'adjustment', 'bucket'].includes(event.type)) {
    throw new Error('Invalid event type');
  }

  // Optional fields validation
  if (event.frequency && !['weekly', 'biweekly', 'monthly', 'yearly'].includes(event.frequency)) {
    throw new Error('Invalid frequency');
  }

  // Only validate savings percentage for payday events
  if (event.type === 'payday' && event.savingsPercentage !== undefined) {
    if (typeof event.savingsPercentage !== 'number' || 
        isNaN(event.savingsPercentage) || 
        event.savingsPercentage < 0 || 
        event.savingsPercentage > 100) {
      throw new Error('Savings percentage must be between 0 and 100');
    }
  }
}

// Function to prepare event data for Firestore
function prepareEventData(event: CalendarEvent) {
  if (!auth.currentUser) {
    throw new Error('User must be logged in');
  }

  try {
    // Ensure we have a valid date
    const normalizedDate = normalizeDate(event.date);

    // Use effective user ID (handles impersonation)
    const userId = getEffectiveUserId() || auth.currentUser.uid;

    // Create a clean object with only the fields we need
    const eventData: Record<string, any> = {
      id: event.id.trim(),
      userId: userId,
      title: event.title.trim(),
      amount: Number(event.amount),
      date: Timestamp.fromDate(normalizedDate),
      type: event.type,
      updatedAt: Timestamp.now(),
      createdAt: event.createdAt ? Timestamp.fromDate(event.createdAt) : Timestamp.now(),
      isRecurring: Boolean(event.isRecurring),
      isPaid: Boolean(event.isPaid)
    };

    // Add optional fields only if they exist
    if (event.category) eventData.category = event.category;
    if (event.frequency) eventData.frequency = event.frequency;
    if (event.recurringGroupId) eventData.recurringGroupId = event.recurringGroupId;
    if (event.debtId) eventData.debtId = event.debtId;
    if (event.subscriptionId) eventData.subscriptionId = event.subscriptionId;
    if (event.vacationPlanId) eventData.vacationPlanId = event.vacationPlanId;
    if (event.adjustmentAmount !== undefined) eventData.adjustmentAmount = event.adjustmentAmount;
    if (event.isAutopay !== undefined) eventData.isAutopay = Boolean(event.isAutopay);
    if (event.isScheduled !== undefined) eventData.isScheduled = Boolean(event.isScheduled);
    if (event.isSubscription !== undefined) eventData.isSubscription = Boolean(event.isSubscription);
    if (event.bucketId) eventData.bucketId = event.bucketId;

    // Only include savingsPercentage for payday events
    if (event.type === 'payday' && event.savingsPercentage !== undefined) {
      eventData.savingsPercentage = Number(event.savingsPercentage);
    }

    // Handle paid date
    if (event.isPaid && event.paidDate) {
      eventData.paidDate = Timestamp.fromDate(normalizeDate(event.paidDate));
    } else {
      eventData.paidDate = null;
    }

    return eventData;
  } catch (error) {
    console.error('Error preparing event data:', error);
    // Use effective user ID (handles impersonation)
    const userId = getEffectiveUserId() || auth.currentUser.uid;
    // Create a minimal valid object as fallback
    return {
      id: event.id,
      userId: userId,
      title: event.title || 'Untitled Event',
      amount: Number(event.amount) || 0,
      date: Timestamp.now(),
      type: event.type || 'bill',
      updatedAt: Timestamp.now(),
      createdAt: Timestamp.now()
    };
  }
}

// Function to handle Firestore errors
function handleFirestoreError(error: unknown): never {
  console.error('Firestore error:', error);
  
  if (error instanceof Error) {
    // Check if the error is related to missing index
    if (error.message.includes('requires an index')) {
      throw new Error('Database index is being created. Please try again in a few minutes.');
    }
    throw error;
  }
  
  throw new Error('An unexpected database error occurred');
}

// Function to get next date based on frequency
function getNextDate(baseDate: Date, frequency: string): Date {
  switch (frequency) {
    case 'weekly':
      return addWeeks(baseDate, 1);
    case 'biweekly':
      return addWeeks(baseDate, 2);
    case 'monthly':
      return addMonths(baseDate, 1);
    case 'yearly':
      return addYears(baseDate, 1);
    default:
      return baseDate;
  }
}

// Function to create recurring events
async function createRecurringEvents(event: CalendarEvent, count: number = 12) {
  if (!auth.currentUser) {
    throw new Error('User must be logged in');
  }

  try {
    validateEventData(event);
    const recurringGroupId = event.recurringGroupId || crypto.randomUUID();
    const batch = writeBatch(db);
    let currentDate = event.date;

    for (let i = 0; i < count; i++) {
      const eventData = prepareEventData({
        ...event,
        id: i === 0 ? event.id : crypto.randomUUID(),
        date: currentDate,
        isRecurring: true,
        recurringGroupId
      });

      const docRef = doc(db, 'events', eventData.id);
      batch.set(docRef, eventData);

      if (i < count - 1) { // Don't calculate next date for the last iteration
        currentDate = getNextDate(currentDate, event.frequency || 'monthly');
      }
    }

    await batch.commit();
  } catch (error) {
    console.error('Error creating recurring events:', error);
    throw new Error('Failed to create recurring events');
  }
}

// Function to add a new event
export async function addEvent(event: CalendarEvent): Promise<string> {
  if (!auth.currentUser) {
    throw new Error('User must be logged in');
  }

  try {
    validateEventData(event);

    if (event.isRecurring && event.frequency) {
      await createRecurringEvents(event);
      return event.id;
    }

    const eventData = prepareEventData(event);
    const eventsRef = collection(db, 'events');
    
    // Use the event ID as the document ID
    await setDoc(doc(eventsRef, event.id), eventData);
    return event.id;
  } catch (error) {
    console.error('Error saving event:', error);
    throw new Error('Failed to save event');
  }
}

// Function to update an existing event
export async function updateEvent(event: CalendarEvent): Promise<void> {
  if (!auth.currentUser) {
    throw new Error('User must be logged in');
  }

  try {
    validateEventData(event);
    const eventRef = doc(db, 'events', event.id);
    const eventDoc = await getDoc(eventRef);

    if (!eventDoc.exists()) {
      throw new Error('Event not found');
    }

    const existingData = eventDoc.data();
    if (existingData?.userId !== auth.currentUser.uid) {
      throw new Error('You do not have permission to update this event');
    }

    const eventData = prepareEventData(event);

    // If this is a recurring event, update all future occurrences
    if (event.isRecurring && event.recurringGroupId) {
      const batch = writeBatch(db);
      
      try {
        // Get all future events in this recurring group
        const eventsRef = collection(db, 'events');
        const q = query(
          eventsRef,
          where('recurringGroupId', '==', event.recurringGroupId),
          where('date', '>=', eventData.date)
        );
        
        const snapshot = await getDocs(q);
        
        snapshot.docs.forEach(doc => {
          const futureEventData = {
            ...eventData,
            date: doc.data().date, // Keep original dates
            id: doc.id // Keep original IDs
          };
          batch.update(doc.ref, futureEventData);
        });

        await batch.commit();
      } catch (error) {
        // If the query fails due to missing index, fall back to updating just this event
        if (error instanceof Error && error.message.includes('requires an index')) {
          console.warn('Falling back to single event update due to missing index');
          await updateDoc(eventRef, eventData);
        } else {
          throw error;
        }
      }
    } else {
      // Update single event
      await updateDoc(eventRef, eventData);
    }
  } catch (error) {
    handleFirestoreError(error);
  }
}

// Function to generate referral codes
function generateReferralCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const length = 8;
  let code = '';
  for (let i = 0; i < length; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

function getAuthErrorMessage(error: Error): string {
  const code = (error as any).code;
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Invalid email or password. Please check your credentials and try again.';
    case 'auth/email-already-in-use':
      return 'This email is already registered. Please sign in or use a different email.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters long.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later or reset your password.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your internet connection and try again.';
    case 'auth/popup-closed-by-user':
      return 'Sign in was cancelled. Please try again.';
    default:
      return error.message || 'An unexpected error occurred';
  }
}